import { Fragment } from 'react';
import { InView } from 'react-intersection-observer';

import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { RiDeleteBinLine, RiStarFill } from '@remixicon/react';

import {
  DateUtils,
  DialogComposite,
  ListEmptyState,
  ResourceTableRow,
  StickyTableContainer,
  StickyTableHead,
} from '@spotify/confidence-core-react';
import { useDeleteCollectionEntryMutation } from '@spotify/confidence-graphql';

import {
  CollectionEntryOptions,
  findCollectionEntryExtension,
  getExtensionDisplayName,
  getExtensionResourceName,
  useCollectionEntryExtensions,
} from '../../extensions';
import { useCollectionEntries, useDeleteCollectionEntry } from '../../hooks';

const useStyles = makeStyles(theme => ({
  table: {
    tableLayout: 'fixed',
    '& > tbody > tr > td': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  starIcon: {
    color: theme.palette.warning.main,
  },
}));

type CollectionEntriesListProps = {
  collectionName: string;
  isFavorites?: boolean;
};

export const CollectionEntriesList = ({
  collectionName,
  isFavorites,
}: CollectionEntriesListProps) => {
  const classes = useStyles();
  const extensions = useCollectionEntryExtensions();

  const {
    collectionEntries,
    isLoading,
    isEmpty,
    error,
    nextPage,
    hasNextPage,
  } = useCollectionEntries({ parent: collectionName });

  const { deleteCollectionEntry, isDeleting } = useDeleteCollectionEntry();
  const [deleteFavoriteEntry] = useDeleteCollectionEntryMutation({
    update: (cache, _, { variables }) => {
      if (variables?.name) {
        cache.evict({
          id: cache.identify({
            __typename: 'CollectionsV1CollectionEntry',
            name: variables.name,
          }),
        });
        cache.gc();
      }
    },
  });

  if (isEmpty) {
    return (
      <ListEmptyState
        title="This collection is empty"
        description="Resources added to this collection will appear here."
      />
    );
  }

  return (
    <Fragment>
      <StickyTableContainer
        loading={isLoading && !collectionEntries?.length}
        error={error}
      >
        <Table className={classes.table}>
          <StickyTableHead>
            <TableRow>
              {isFavorites && <TableCell width={48} padding="checkbox" />}
              <TableCell>Name</TableCell>
              <TableCell width="15%">Type</TableCell>
              <TableCell width="20%">Added</TableCell>
              {!isFavorites && <TableCell width={48} padding="checkbox" />}
            </TableRow>
          </StickyTableHead>
          <TableBody>
            {collectionEntries?.map(entry => {
              const extension = findCollectionEntryExtension(extensions, entry);
              const resource = entry.resource as
                | CollectionEntryOptions['resource']
                | undefined;
              const displayName = resource
                ? getExtensionDisplayName(extension, resource, entry)
                : entry.name;
              const resourceType = extension?.resourceType ?? 'Unknown';

              const cells = (
                <>
                  {isFavorites && (
                    <TableCell
                      padding="checkbox"
                      onClick={e => e.stopPropagation()}
                    >
                      <IconButton
                        size="small"
                        className={classes.starIcon}
                        onClick={() =>
                          deleteFavoriteEntry({
                            variables: { name: entry.name },
                          })
                        }
                      >
                        <RiStarFill size={18} />
                      </IconButton>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body1">{displayName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" color="textSecondary">
                      {resourceType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" color="textSecondary">
                      {entry.createTime
                        ? DateUtils.xAgo(new Date(entry.createTime))
                        : '-'}
                    </Typography>
                  </TableCell>
                  {!isFavorites && (
                    <TableCell
                      padding="checkbox"
                      onClick={e => e.stopPropagation()}
                    >
                      <DialogComposite.Root>
                        <DialogComposite.Trigger>
                          <IconButton size="small" disabled={isDeleting}>
                            <RiDeleteBinLine size={18} />
                          </IconButton>
                        </DialogComposite.Trigger>
                        <DialogComposite.Content>
                          <DialogComposite.Header>
                            <DialogComposite.Title>
                              Remove from collection
                            </DialogComposite.Title>
                            <DialogComposite.CloseButton />
                          </DialogComposite.Header>
                          <DialogComposite.Body>
                            <Typography>
                              Are you sure you want to remove{' '}
                              <strong>{displayName}</strong> from this
                              collection?
                            </Typography>
                          </DialogComposite.Body>
                          <DialogComposite.Actions>
                            <DialogComposite.Actions.Cancel>
                              Cancel
                            </DialogComposite.Actions.Cancel>
                            <DialogComposite.Actions.Primary
                              onClick={() => deleteCollectionEntry(entry.name)}
                              disabled={isDeleting}
                            >
                              Remove
                            </DialogComposite.Actions.Primary>
                          </DialogComposite.Actions>
                        </DialogComposite.Content>
                      </DialogComposite.Root>
                    </TableCell>
                  )}
                </>
              );

              // Use extension's routeRef if available, otherwise render non-navigable row
              if (extension && resource) {
                const resourceName = getExtensionResourceName(
                  extension,
                  resource,
                );
                return (
                  <ResourceTableRow
                    key={entry.name}
                    routeRef={extension.routeRef}
                    name={resourceName}
                  >
                    {cells}
                  </ResourceTableRow>
                );
              }

              return <TableRow key={entry.name}>{cells}</TableRow>;
            })}
          </TableBody>
        </Table>
      </StickyTableContainer>
      {hasNextPage && (
        <InView
          onChange={async inView => {
            if (inView) await nextPage();
          }}
        />
      )}
    </Fragment>
  );
};
